<template>
  <div class="fee-rules-page">
    <div class="page-header">
      <h2 class="page-title">订单收费计算规则</h2>
      <button @click="showAddModal = true" class="add-btn">+ 添加规则</button>
    </div>

    <div class="search-bar">
      <input type="text" v-model="searchKeyword" placeholder="搜索规则名称" class="search-input" @keyup.enter="loadRules" />
      <select v-model="searchFeeType" class="search-select" @change="loadRules">
        <option value="">全部收费类型</option>
        <option :value="1">起步价</option>
        <option :value="2">距离费</option>
        <option :value="4">楼层费</option>
        <option :value="5">特殊时段费</option>
        <option :value="6">特殊天气费</option>
      </select>
      <select v-model="searchServiceType" class="search-select" @change="loadRules">
        <option value="">全部服务类型</option>
        <option :value="0">全部</option>
        <option :value="1">代送物品</option>
        <option :value="2">代买物品</option>
        <option :value="3">代取快递</option>
        <option :value="4">其他</option>
      </select>
      <select v-model="searchStatus" class="search-select" @change="loadRules">
        <option value="">全部状态</option>
        <option :value="1">启用</option>
        <option :value="0">禁用</option>
      </select>
      <button @click="loadRules" class="search-btn">搜索</button>
    </div>

    <table class="rules-table">
      <thead>
        <tr>
          <th>ID</th>
          <th>规则名称</th>
          <th>收费类型</th>
          <th>服务类型</th>
          <th>计算方式</th>
          <th>起步费用</th>
          <th>优先级</th>
          <th>有效期</th>
          <th>状态</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="rule in rules" :key="rule.id">
          <td>{{ rule.id }}</td>
          <td>{{ rule.rule_name }}</td>
          <td>{{ getFeeTypeText(rule.fee_type) }}</td>
          <td>{{ getServiceTypeText(rule.service_type_code) }}</td>
          <td>{{ getCalculationTypeText(rule.calculation_type) }}</td>
          <td>{{ (rule.start_fee / 100).toFixed(2) }}</td>
          <td>{{ rule.priority }}</td>
          <td>{{ formatDateRange(rule.effective_time, rule.expire_time) }}</td>
          <td>
            <span @click="toggleStatus(rule)" :class="['status-badge', 'clickable-status', rule.status === 1 ? 'status-enabled' : 'status-disabled']">
              {{ rule.status === 1 ? '启用' : '禁用' }}
            </span>
          </td>
          <td>
            <button @click="viewRule(rule)" class="view-btn">查看</button>
            <button @click="editRule(rule)" class="edit-btn">编辑</button>
            <button @click="deleteRule(rule.id)" class="delete-btn">删除</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="rules.length === 0" class="empty-state">
      <span class="empty-icon">📭</span>
      <p>暂无收费计算规则</p>
      <button @click="showAddModal = true" class="empty-add-btn">添加第一条规则</button>
    </div>

    <!-- 分页 -->
    <div v-if="total > 0" class="pagination">
      <button @click="prevPage" :disabled="page <= 1" class="page-btn">上一页</button>
      <span class="page-info">第 {{ page }} / {{ totalPages }} 页</span>
      <button @click="nextPage" :disabled="page >= totalPages" class="page-btn">下一页</button>
    </div>

    <!-- 添加/编辑弹窗 -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ showEditModal ? '编辑规则' : '添加规则' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-item">
            <label class="form-label">规则名称 <span class="required">*</span></label>
            <input type="text" v-model="formData.rule_name" class="form-input" placeholder="请输入规则名称" />
          </div>

          <div class="form-row">
            <div class="form-item">
              <label class="form-label">收费类型 <span class="required">*</span></label>
              <select v-model="formData.fee_type" class="form-select" @change="onFeeTypeChange">
                <option :value="1">起步价</option>
                <option :value="2">距离费</option>
                <option :value="4">楼层费</option>
                <option :value="5">特殊时段费</option>
                <option :value="6">特殊天气费</option>
              </select>
            </div>
            <div class="form-item">
              <label class="form-label">服务类型 <span class="required">*</span></label>
              <select v-model="formData.service_type" class="form-select">
                <option :value="0">全部</option>
                <option :value="1">代送物品</option>
                <option :value="2">代买物品</option>
                <option :value="3">代取快递</option>
                <option :value="4">其他</option>
              </select>
            </div>
          </div>

          <div class="form-item" v-if="formData.fee_type !== 2 && formData.fee_type !== 5 && formData.fee_type !== 6">
            <label class="form-label">计算方式 <span class="required">*</span></label>
            <select v-model="formData.calculation_type" class="form-select" :disabled="isCalculationTypeLocked" @change="onCalculationTypeChange">
              <option :value="1">按固定金额计费</option>
              <option :value="3" :disabled="isCalculationTypeLocked">按距离计费</option>
              <option :value="4" :disabled="isCalculationTypeLocked">时段规则</option>
              <option :value="5" :disabled="isCalculationTypeLocked">天气规则</option>
              <option :value="6" :disabled="isCalculationTypeLocked">按楼层计费</option>
            </select>
            <span class="form-hint" v-if="isCalculationTypeLocked">收费类型为「起步价」或「楼层费」时，计算方式已固定，不可更改。</span>
          </div>

          <div class="form-row" v-if="formData.fee_type !== 2 && formData.fee_type !== 4 && formData.fee_type !== 5 && formData.fee_type !== 6">
            <div class="form-item">
              <label class="form-label">起步价（单位：元）</label>
              <input type="text" inputmode="decimal" v-model="formData.start_fee" class="form-input" placeholder="请输入起步价" @blur="formatAmount($event, formData, 'start_fee')" />
              <span class="form-hint">单位：元，系统自动转换为分</span>
            </div>
          </div>

          <!-- 楼层费单价（fee_type=4 时使用），单位：元，保存时自动转为分 -->
          <div v-if="formData.fee_type === 4" class="form-row">
            <div class="form-item">
              <label class="form-label">每层楼层费（元）</label>
              <input type="text" inputmode="decimal" v-model="formData.floor_fee" class="form-input" placeholder="请输入每层楼层费" @blur="formatAmount($event, formData, 'floor_fee')" />
              <span class="form-hint">单位：元，系统自动转换为分；楼层费总额 = 每层费用 × (取货楼层 + 送货楼层)，楼层数暂无数据源默认 0</span>
            </div>
          </div>

          <!-- 距离计费规则配置 -->
          <div v-if="formData.calculation_type === 3 && formData.fee_type !== 5" class="form-item">
            <label class="form-label">距离计费规则 [ 距离下限（米）-距离上限（米） 基础费用（元） 每100米加价（元） ]</label>
            <div class="distance-rules-container">
              <div v-for="(rule, index) in distanceRules" :key="index" class="distance-rule-item">
                <div class="distance-row">
                  <input type="number" v-model="rule.distance_min" class="form-input distance-input" placeholder="距离下限（米）" />
                  <span class="distance-separator">-</span>
                  <input type="number" v-model="rule.distance_max" class="form-input distance-input" placeholder="距离上限（0表示无上限）" />
                  <input type="text" inputmode="decimal" v-model="rule.base_distance_fee" class="form-input distance-input" placeholder="基础费用（元）" @blur="formatAmount($event, rule, 'base_distance_fee')" />
                  <input type="text" inputmode="decimal" v-model="rule.per_100m" class="form-input distance-input" placeholder="每100米加价（元）" @blur="formatAmount($event, rule, 'per_100m')" />
                  <button v-if="distanceRules.length > 1" @click="removeDistanceRule(index)" class="remove-distance-btn">×</button>
                </div>
              </div>
              <button @click="addDistanceRule" class="add-distance-btn">+ 添加距离段</button>
            </div>
          </div>

          <!-- 特殊时段费配置（fee_type=5 时使用） -->
          <div v-if="formData.fee_type === 5" class="form-item">
            <label class="form-label">时段规则 [ 开始时间 - 结束时间（跨午夜如 22:00-06:00） 费用（元） ]</label>
            <div class="time-rules-container">
              <div v-for="(rule, index) in timeRules" :key="index" class="time-rule-item">
                <div class="time-row">
                  <input type="time" v-model="rule.start_time" class="form-input time-input" />
                  <span class="time-separator">-</span>
                  <input type="time" v-model="rule.end_time" class="form-input time-input" />
                  <input type="text" inputmode="decimal" v-model="rule.fee" class="form-input time-input" placeholder="费用（元）" @blur="formatAmount($event, rule, 'fee')" />
                  <button v-if="timeRules.length > 1" @click="removeTimeRule(index)" class="remove-time-btn">×</button>
                </div>
              </div>
              <button @click="addTimeRule" class="add-time-btn">+ 添加时段</button>
            </div>
            <span class="form-hint">多个时段可同时生效，命中时段的费用叠加；跨午夜时段 start_time &gt; end_time（如 22:00-06:00）</span>
          </div>

          <!-- 特殊天气费配置（fee_type=6 且 calculation_type=5 时使用） -->
          <div v-if="formData.fee_type === 6 && formData.calculation_type === 5" class="form-item">
            <label class="form-label">天气加价规则 [ 天气类型 加价金额（元） ]</label>
            <div class="weather-rules-container">
              <div v-for="(item, index) in weatherRules" :key="index" class="weather-rule-item">
                <div class="weather-row">
                  <select v-model="item.weather_type_code" class="form-input weather-input">
                    <option value="">请选择天气</option>
                    <option v-for="wt in weatherTypeOptions" :key="wt.code" :value="wt.code">{{ wt.name }}({{ wt.code }})</option>
                  </select>
                  <input type="text" inputmode="decimal" v-model="item.fee" class="form-input weather-input" placeholder="加价（元）" @blur="formatAmount($event, item, 'fee')" />
                  <button v-if="weatherRules.length > 1" @click="removeWeatherRule(index)" class="remove-weather-btn">×</button>
                </div>
              </div>
              <button @click="addWeatherRule" class="add-weather-btn">+ 添加天气加价</button>
            </div>
            <span class="form-hint">当前订单天气命中多条配置时，取加价费用的最大值。</span>
          </div>

          <div class="form-row">
            <div class="form-item">
              <label class="form-label">生效时间</label>
              <input type="date" v-model="formData.effective_time" class="form-input" />
            </div>
            <div class="form-item">
              <label class="form-label">过期时间（不填表示永久有效）</label>
              <input type="date" v-model="formData.expire_time" class="form-input" placeholder="不填表示永久有效" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-item">
              <label class="form-label">优先级（数值越小越优先级越高） <span class="required">*</span></label>
              <input type="number" v-model="formData.priority" class="form-input" placeholder="数值越小优先级越高" />
            </div>
            <div class="form-item">
              <label class="form-label">排序（数值越小越靠前）</label>
              <input type="number" v-model="formData.sort_order" class="form-input" placeholder="数值越小越靠前" />
            </div>
          </div>

          <div class="form-item">
            <label class="form-label">状态 <span class="required">*</span></label>
            <select v-model="formData.status" class="form-select">
              <option :value="1">启用</option>
              <option :value="0">禁用</option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-item">
              <label class="form-label">适用商家 ID</label>
              <input type="number" v-model="formData.shop_id" class="form-input" placeholder="为空表示所有商家" />
            </div>
            <div class="form-item">
              <label class="form-label">适用城市 ID</label>
              <input type="number" v-model="formData.city_id" class="form-input" placeholder="为空表示所有城市" />
            </div>
            <div class="form-item">
              <label class="form-label">适用小区 ID</label>
              <input type="number" v-model="formData.community_id" class="form-input" placeholder="为空表示所有小区" />
            </div>
          </div>

          <div class="form-item">
            <label class="form-label">描述</label>
            <textarea v-model="formData.description" class="form-textarea" placeholder="请输入规则描述" rows="3"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="cancel-btn">取消</button>
          <button @click="saveRule" class="confirm-btn">{{ showEditModal ? '保存修改' : '确认添加' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from '@/api/request'

export default {
  name: 'FeeRules',
  data() {
    return {
      rules: [],
      searchKeyword: '',
      searchFeeType: '',
      searchServiceType: '',
      searchStatus: '',
      page: 1,
      pageSize: 20,
      total: 0,
      showAddModal: false,
      showEditModal: false,
      editingId: null,
      formData: {
        rule_name: '',
        fee_type: 1,
        service_type: 0,
        calculation_type: 1,
        start_fee: '0.00',
        floor_fee: '0.00',
        distance_rules: [],
        effective_time: new Date().toISOString().slice(0, 10),
        expire_time: '',
        priority: 0,
        sort_order: 0,
        status: 1,
        shop_id: '',
        city_id: '',
        community_id: '',
        description: ''
      },
      distanceRules: [],
      timeRules: [],
      weatherRules: [],
      weatherTypeOptions: []
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    },
    // 收费类型为「起步价」(fee_type=1) 或「楼层费」(fee_type=4) 时，
    // 计算方式分别锁定为「按固定金额计费」(calculation_type=1) / 「按楼层计费」(calculation_type=6)，不可改选
    isCalculationTypeLocked() {
      return [1, 4].includes(Number(this.formData.fee_type))
    }
  },
  mounted() {
    this.loadRules()
    this.loadWeatherTypeOptions()
  },
  methods: {
    async loadWeatherTypeOptions() {
      try {
        const res = await request({ url: '/admin/weather-types/options', method: 'get' })
        if (res.code === 0) {
          this.weatherTypeOptions = (res.data.weather_types || []).map(w => ({ code: w.code, name: w.name }))
        }
      } catch (e) {
        console.error('加载天气类型选项失败', e)
      }
    },
    async loadRules() {
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize
        }
        if (this.searchKeyword) params.keyword = this.searchKeyword
        if (this.searchFeeType) params.fee_type = this.searchFeeType
        if (this.searchServiceType) params.service_type_code = this.serviceTypeIntToCode(this.searchServiceType)
        if (this.searchStatus) params.status = this.searchStatus

        const result = await request({
          url: '/admin/fee-rules',
          method: 'get',
          params
        })

        if (result.code === 0) {
          this.rules = result.data.list || result.data
          this.total = result.data.total || this.rules.length
        } else {
        }
      } catch (error) {
      }
    },
    prevPage() {
      if (this.page > 1) {
        this.page--
        this.loadRules()
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.loadRules()
      }
    },
    getFeeTypeText(type) {
      const map = { 1: '起步价', 2: '距离费', 3: '搬运费', 4: '楼层费', 5: '特殊时段费', 6: '特殊天气费' }
      return map[type] || '未知'
    },
    // 前端用整数 service_type（0/1/2/3/4），后端存储为 service_type_code（ALL/DELIVERY/PURCHASE/PICKUP/OTHER）
    serviceTypeIntToCode(type) {
      const map = { 0: 'ALL', 1: 'DELIVERY', 2: 'PURCHASE', 3: 'PICKUP', 4: 'OTHER' }
      return map[type] != null ? map[type] : 'ALL'
    },
    serviceTypeCodeToInt(code) {
      const map = { ALL: 0, DELIVERY: 1, PURCHASE: 2, PICKUP: 3, OTHER: 4 }
      return map[code] != null ? map[code] : 0
    },
    getServiceTypeText(code) {
      const map = { ALL: '全部', DELIVERY: '代送物品', PURCHASE: '代买物品', PICKUP: '代取快递', OTHER: '其他' }
      return map[code] || '未知'
    },
    getCalculationTypeText(type) {
      const map = { 1: '按固定金额计费', 3: '按距离计费', 4: '时段规则', 5: '天气规则', 6: '按楼层计费' }
      return map[type] || '未知'
    },
    formatDistanceRange(min, max) {
      const minText = min === 0 ? '不限' : min
      const maxText = max === 0 ? '不限' : max
      return `${minText} - ${maxText}`
    },
    formatDateRange(start, end) {
      if (!start) return '永久生效'
      const startDate = new Date(start).toLocaleDateString('zh-CN')
      const endDate = end ? new Date(end).toLocaleDateString('zh-CN') : '永久'
      return `${startDate} ~ ${endDate}`
    },
    onCalculationTypeChange() {
      this.distanceRules = []
      if (this.formData.calculation_type === 3) {
        this.distanceRules = [{ distance_min: 0, distance_max: 0, base_distance_fee: '0.00', per_100m: '0.00' }]
      }
    },
    addDistanceRule() {
      this.distanceRules.push({ distance_min: 0, distance_max: 0, base_distance_fee: '0.00', per_100m: '0.00' })
    },
    removeDistanceRule(index) {
      this.distanceRules.splice(index, 1)
    },
    onFeeTypeChange() {
      if (Number(this.formData.fee_type) !== 5) {
        this.timeRules = []
      }
      if (Number(this.formData.fee_type) !== 6) {
        this.weatherRules = []
      }
      // 收费类型为「起步价」(fee_type=1) 时，计算方式锁定为「按固定金额计费」
      if (Number(this.formData.fee_type) === 1) {
        this.formData.calculation_type = 1
        return
      }
      // 收费类型为「楼层费」(fee_type=4) 时，计算方式锁定为「按楼层计费」
      if (Number(this.formData.fee_type) === 4) {
        this.formData.calculation_type = 6
        return
      }
      // 距离费（fee_type=2）、特殊时段费（fee_type=5）、特殊天气费（fee_type=6）
      // 隐藏基础费用/距离/物品总额输入框，相关字段自动置 0，计算方式自动设定
      if ([2, 5, 6].includes(Number(this.formData.fee_type))) {
        if (Number(this.formData.fee_type) === 6) {
          this.formData.calculation_type = 5
        } else if (Number(this.formData.fee_type) === 5) {
          this.formData.calculation_type = 4
        } else if (Number(this.formData.fee_type) === 2) {
          this.formData.calculation_type = 3
        }
        this.formData.start_fee = '0.00'
      }
    },
    addTimeRule() {
      this.timeRules.push({ start_time: '00:00', end_time: '00:00', fee: '0.00' })
    },
    removeTimeRule(index) {
      this.timeRules.splice(index, 1)
    },
    // 金额输入框失焦时，统一格式化为 2 位小数显示（如 5 -> 5.00）
    formatAmount(e, target, key) {
      let val = parseFloat(e.target.value)
      if (isNaN(val)) val = 0
      this.$set(target, key, val.toFixed(2))
    },
    // 将后端返回的 JSON 字段安全转为数组。
    // 兼容数据库二次 JSON 编码（字段里存的是字符串形式的数组）的情况。
    parseJsonField(val) {
      if (Array.isArray(val)) return val.map(item => ({ ...item }))
      if (typeof val === 'string' && val.trim()) {
        try {
          let parsed = JSON.parse(val)
          if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed)
          }
          return Array.isArray(parsed) ? parsed.map(item => ({ ...item })) : []
        } catch (e) {
          return []
        }
      }
      return []
    },
    viewRule(rule) {
      this.$router.push(`/orders/fee-rules/detail/${rule.id}`)
    },
    editRule(rule) {
      this.editingId = rule.id
      this.formData = {
        rule_name: rule.rule_name,
        fee_type: rule.fee_type,
        service_type: this.serviceTypeCodeToInt(rule.service_type_code),
        calculation_type: rule.calculation_type,
        start_fee: rule.start_fee != null ? (rule.start_fee / 100).toFixed(2) : '',
        floor_fee: rule.floor_fee != null ? (rule.floor_fee / 100).toFixed(2) : '',
        distance_rules: rule.distance_rules || [],
        effective_time: rule.effective_time ? new Date(rule.effective_time).toISOString().slice(0, 10) : '',
        expire_time: rule.expire_time ? new Date(rule.expire_time).toISOString().slice(0, 10) : '',
        priority: rule.priority || 0,
        sort_order: rule.sort_order || 0,
        status: rule.status,
        shop_id: rule.shop_id || '',
        city_id: rule.city_id || '',
        community_id: rule.community_id || '',
        description: rule.description || ''
      }
      // 库里 distance_rules 的 base_distance_fee / per_100m 存的是「分」，回显为「元」
      this.distanceRules = this.parseJsonField(rule.distance_rules).map(r => ({
        distance_min: r.distance_min || 0,
        distance_max: r.distance_max || 0,
        base_distance_fee: r.base_distance_fee != null ? (r.base_distance_fee / 100).toFixed(2) : '0.00',
        per_100m: r.per_100m != null ? (r.per_100m / 100).toFixed(2) : '0.00'
      }))
      // 库里 time_rules 的 fee 存的是「分」，回显为「元」
      const parsedTimeRules = this.parseJsonField(rule.time_rules)
      this.timeRules = parsedTimeRules.length
        ? parsedTimeRules.map(t => ({
            start_time: t.start_time || '00:00',
            end_time: t.end_time || '00:00',
            fee: t.fee != null ? (t.fee / 100).toFixed(2) : '0.00'
          }))
        : (Number(rule.fee_type) === 5 ? [{ start_time: '00:00', end_time: '00:00', fee: '0.00' }] : [])
      // 库里 weather_rules 的 fee 存的是「分」，回显为「元」
      const parsedWeatherRules = this.parseJsonField(rule.weather_rules)
      this.weatherRules = parsedWeatherRules.length
        ? parsedWeatherRules.map(w => ({
            weather_type_code: w.weather_type_code || '',
            fee: w.fee != null ? (w.fee / 100).toFixed(2) : '0.00'
          }))
        : (Number(rule.fee_type) === 6 && Number(rule.calculation_type) === 5 ? [{ weather_type_code: '', fee: '0.00' }] : [])
      this.showEditModal = true
    },
    async toggleStatus(rule) {
      const newStatus = rule.status === 1 ? 0 : 1
      const statusText = newStatus === 1 ? '启用' : '禁用'
      if (!confirm(`确定要${statusText}规则「${rule.rule_name}」吗？`)) return

      try {
        const result = await request({
          url: `/admin/fee-rules/${rule.id}`,
          method: 'put',
          data: { status: newStatus }
        })

        if (result.code === 0) {
          rule.status = newStatus
          alert(`${statusText}成功`)
        } else {
          alert(`${statusText}失败: ` + result.message)
        }
      } catch (error) {
        alert(`${statusText}失败`)
      }
    },
    async deleteRule(id) {
      if (!confirm('确定要删除这条规则吗？')) return

      try {
        const result = await request({
          url: `/admin/fee-rules/${id}`,
          method: 'delete'
        })

        if (result.code === 0) {
          alert('删除成功')
          this.loadRules()
        } else {
          alert('删除失败: ' + result.message)
        }
      } catch (error) {
        alert('删除失败')
      }
    },
    closeModal() {
      this.showAddModal = false
      this.showEditModal = false
      this.editingId = null
      this.formData = {
        rule_name: '',
        fee_type: 1,
        service_type: 0,
        calculation_type: 1,
        start_fee: '0.00',
        floor_fee: '0.00',
        distance_rules: [],
        effective_time: new Date().toISOString().slice(0, 10),
        expire_time: '',
        priority: 0,
        sort_order: 0,
        status: 1,
        shop_id: '',
        city_id: '',
        community_id: '',
        description: ''
      }
      this.distanceRules = []
      this.timeRules = []
      this.weatherRules = []
    },
    addWeatherRule() {
      this.weatherRules.push({ weather_type_code: '', fee: '0.00' })
    },
    removeWeatherRule(index) {
      this.weatherRules.splice(index, 1)
    },
    async saveRule() {
      if (!this.formData.rule_name) {
        alert('请输入规则名称')
        return
      }

      const data = { ...this.formData }

      // 服务类型：前端用整数 service_type，后端表字段为 service_type_code（字符串）
      data.service_type_code = this.serviceTypeIntToCode(this.formData.service_type)
      delete data.service_type

      // fee_type=4（楼层费）不使用 start_fee，强制置 0（避免遗留旧值误入库）
      if (Number(data.fee_type) === 4) {
        data.start_fee = 0
      }

      // 转换金额单位（元转分），空值转为0
      if (data.start_fee === '' || data.start_fee === null) {
        data.start_fee = 0
      } else {
        data.start_fee = Math.round(parseFloat(data.start_fee) * 100)
      }
      if (data.floor_fee === '' || data.floor_fee === null) {
        data.floor_fee = 0
      } else {
        data.floor_fee = Math.round(parseFloat(data.floor_fee) * 100)
      }
      // 设置 JSON 规则
      if (data.calculation_type === 3) {
        // 距离段内 base_distance_fee / per_100m 以「元」录入，保存时统一转为「分」
        data.distance_rules = JSON.stringify(this.distanceRules.map(r => ({
          distance_min: r.distance_min || 0,
          distance_max: r.distance_max || 0,
          base_distance_fee: Math.round(parseFloat(r.base_distance_fee || 0) * 100),
          per_100m: Math.round(parseFloat(r.per_100m || 0) * 100)
        })))
      } else {
        data.distance_rules = null
      }

      // 特殊时段费 time_rules（fee_type=5 时录入，单位元转分；其余类型置空）
      if (Number(data.fee_type) === 5) {
        data.time_rules = JSON.stringify(this.timeRules.map(t => ({
          start_time: t.start_time || '00:00',
          end_time: t.end_time || '00:00',
          fee: Math.round(parseFloat(t.fee || 0) * 100)
        })))
      } else {
        data.time_rules = null
      }

      // 特殊天气费 weather_rules（fee_type=6 且 calculation_type=5 时录入，单位元转分；其余类型置空）
      if (Number(data.fee_type) === 6 && Number(data.calculation_type) === 5) {
        data.weather_rules = JSON.stringify(this.weatherRules
          .filter(w => w.weather_type_code)
          .map(w => ({
            weather_type_code: w.weather_type_code,
            fee: Math.round(parseFloat(w.fee || 0) * 100)
          })))
      } else {
        data.weather_rules = null
      }

      // 空值处理
      if (!data.shop_id) data.shop_id = null
      if (!data.city_id) data.city_id = null
      if (!data.community_id) data.community_id = null
      if (!data.expire_time) data.expire_time = null
      
      // 日期格式处理：添加时间部分
      if (data.effective_time && data.effective_time.length === 10) {
        data.effective_time = data.effective_time + ' 00:00:00'
      }
      if (data.expire_time && data.expire_time.length === 10) {
        data.expire_time = data.expire_time + ' 00:00:00'
      }

      try {
        let result
        if (this.editingId) {
          result = await request({
            url: `/admin/fee-rules/${this.editingId}`,
            method: 'put',
            data
          })
        } else {
          result = await request({
            url: '/admin/fee-rules',
            method: 'post',
            data
          })
        }

        if (result.code === 0) {
          alert(this.editingId ? '修改成功' : '添加成功')
          this.closeModal()
          this.loadRules()
        } else {
          alert('操作失败: ' + result.message + (result.data ? '\n详情: ' + JSON.stringify(result.data) : ''))
        }
      } catch (error) {
        alert('保存失败: ' + (error.response ? error.response.data?.message || error.message : error.message))
      }
    }
  }
}
</script>

<style scoped>
.fee-rules-page {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.add-btn {
  padding: 10px 20px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.3s;
}

.add-btn:hover {
  background: #5a6fd6;
}

.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.search-input {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  min-width: 200px;
}

.search-select {
  padding: 10px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  background: #fff;
}

.search-btn {
  padding: 10px 20px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.rules-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.rules-table th,
.rules-table td {
  padding: 14px 2px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 14px;
}

.rules-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.rules-table tr:hover {
  background: #f8f9fa;
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}

.status-enabled {
  background: #d4edda;
  color: #155724;
}

.status-disabled {
  background: #f8d7da;
  color: #721c24;
}

.clickable-status {
  cursor: pointer;
  transition: all 0.2s;
}

.clickable-status:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

.edit-btn,
.delete-btn,
.view-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  margin-right: 8px;
  transition: opacity 0.3s;
}

.view-btn {
  background: #95a5a6;
  color: #fff;
}

.edit-btn {
  background: #667eea;
  color: #fff;
}

.delete-btn {
  background: #e74c3c;
  color: #fff;
}

.edit-btn:hover,
.delete-btn:hover,
.view-btn:hover {
  opacity: 0.8;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.empty-icon {
  font-size: 48px;
  display: block;
  margin-bottom: 16px;
}

.empty-state p {
  color: #999;
  margin: 0 0 20px 0;
}

.empty-add-btn {
  padding: 10px 24px;
  background: #667eea;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.form-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.form-row .form-item {
  flex: 1;
  min-width: 200px;
}

.form-item {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}

.required {
  color: #e74c3c;
}

.form-input,
.form-select,
.form-textarea {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: #fff;
}

.form-textarea {
  resize: vertical;
}

.form-input:focus,
.form-select:focus,
.form-textarea:focus {
  outline: none;
  border-color: #667eea;
}

.form-hint {
  display: block;
  font-size: 12px;
  color: #999;
  margin-top: 6px;
}

/* 规则容器样式 */
.distance-rules-container,
.time-rules-container,
.weather-rules-container {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 12px;
}

.distance-rule-item,
.time-rule-item,
.weather-rule-item {
  margin-bottom: 12px;
}

.tier-rule-item:last-child,
.distance-rule-item:last-child,
.weather-rule-item:last-child {
  margin-bottom: 0;
}

.tier-row,
.distance-row,
.time-row,
.weather-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.tier-input,
.distance-input,
.time-input,
.weather-input {
  flex: 1;
  min-width: 100px;
}

.tier-separator,
.distance-separator,
.time-separator {
  font-weight: bold;
  color: #666;
}

.remove-tier-btn,
.remove-distance-btn,
.remove-time-btn,
.remove-weather-btn {
  width: 32px;
  height: 32px;
  background: #f0f0f0;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
}

.add-tier-btn,
.add-distance-btn,
.add-time-btn,
.add-weather-btn {
  width: 100%;
  padding: 8px;
  background: #f8f9fa;
  border: 1px dashed #ddd;
  border-radius: 4px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  margin-top: 8px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.cancel-btn,
.confirm-btn {
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.cancel-btn {
  background: #f0f0f0;
  color: #666;
}

.confirm-btn {
  background: #667eea;
  color: #fff;
}

.cancel-btn:hover {
  background: #e0e0e0;
}

.confirm-btn:hover {
  background: #5a6fd6;
}
</style>